import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useT } from '@/contexts/I18nContext';
import { getInviteInfo, redeemInvite, type InviteInfoDto } from '@/api/invites';
import { extractApiError } from '@/utils/apiError';

/** /invite/<token> — short-circuits for unauthenticated visitors,
 *  shows a one-tap "accept invitation" for authenticated ones.
 *
 *  Unauthenticated users are forwarded straight to /auth (which
 *  fetches the same invite info, shows it as a greeting, and
 *  handles both TG sign-in and password sign-up/sign-in). Keeping
 *  a separate "I have an account" / "Create one" fork on this page
 *  duplicated UI — the auth page already exposes both paths in a
 *  cleaner way.
 *
 *  Authenticated users skip the round-trip — there's no auth
 *  decision to make, just "yes accept this invite". */
const RedeemPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t } = useT();
  const navigate = useNavigate();

  const isLoggedIn = Boolean(localStorage.getItem('jwt'));

  // Unauthenticated → forward to /auth with this invite as the next
  // target. The auth page will fetch the same invite info and show
  // the same greeting; no point in showing it twice.
  useEffect(() => {
    if (!token || isLoggedIn) return;
    window.location.replace(`/auth?next=${encodeURIComponent(`/invite/${token}`)}`);
  }, [token, isLoggedIn]);

  const [info, setInfo] = useState<InviteInfoDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !isLoggedIn) return;
    getInviteInfo(token)
      .then(r => setInfo(r.data))
      .catch(err => setError(extractApiError(err, t('invite.invalid'))))
      .finally(() => setLoading(false));
  }, [token, t, isLoggedIn]);

  async function handleAccept() {
    if (!token) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await redeemInvite(token, {});
      localStorage.setItem('jwt', res.data.access);
      if (res.data.refresh) localStorage.setItem('refresh', res.data.refresh);
      navigate(`/usage/dashboard?zone=${res.data.zone_id}`, { replace: true });
    } catch (err) {
      setSubmitError(extractApiError(err, t('error.general')));
    } finally {
      setSubmitting(false);
    }
  }

  // Render nothing while the redirect is in flight (the effect above
  // navigates synchronously on the next tick; flashing a card would
  // be worse than a brief blank).
  if (!isLoggedIn) return null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardContent className="space-y-4 pt-6">
          {loading && (
            <p className="text-center text-sm text-muted-foreground">{t('common.loading')}</p>
          )}

          {error && (
            <div className="space-y-3 text-center">
              <p className="text-sm font-medium text-destructive">{error}</p>
              <Button variant="outline" size="sm" onClick={() => navigate('/usage/dashboard')}>
                {t('invite.goToDashboard')}
              </Button>
            </div>
          )}

          {info && !error && (
            <>
              <div className="space-y-1 text-center">
                <h1 className="text-lg font-semibold">{t('invite.title')}</h1>
                <p className="text-sm text-muted-foreground">
                  {t('invite.description', {
                    zone: info.zone_name,
                    role: t(`role.${info.role}`),
                  })}
                </p>
              </div>

              {submitError && <p className="text-sm text-destructive">{submitError}</p>}
              <Button className="w-full" onClick={handleAccept} disabled={submitting}>
                {submitting ? t('common.saving') : t('invite.accept')}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RedeemPage;
